import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Equipment Planning Partner Content) ---
import {
    // Core Concepts & General
    faMicroscope,       // Medical Equipment / Expertise
    faTools,            // Planning / Process / Installation / Maintenance
    faLightbulb,        // Future-Oriented / Innovation / Strategy
    faDollarSign,       // Cost-Effective / Budget / Finance
    faCog,              // Customized / Solutions / Integration / Pre-installation
    faHandshake,        // Vendor Partnerships / Procurement / Consultation
    faLaptopCode,       // IT / PACS Integration
    faCheckCircle,      // Quality Assurance / Completion
    // Process Steps
    faComments,         // Consultation / Needs Assessment
    faSitemap,          // Department-Wise Planning
    faWeightHanging,    // Load Calculations / Space Planning (faRulerCombined could also work)
    faShoppingCart,     // Vendor Selection / Procurement
    // faCog already listed for Pre-installation
    faWrench,           // Installation Supervision
    faClipboardList,    // Inventory Audit
    faNetworkWired,     // Post-Installation Integration (IT/PACS focus)
    faChalkboardTeacher,// Training & Support
    // Value Prop / Differentiators Icons (reuse some from above)
    faUserTie,          // Expertise (Comprehensive Knowledge)
    // faLightbulb already listed (Future-Oriented)
    // faDollarSign already listed (Cost-Effective)
    // faCog already listed (Customized Selection)
    faCalculator,       // Comprehensive Budget Planning
    // faHandshake already listed (Vendor Partnerships / Quality)
    // faLaptopCode already listed (Seamless Integration)
    // Service Summary Icons (reuse from above)
    // faTools, faCalculator, faCog, faHandshake, faWrench
    faLifeRing,         // Maintenance and Support
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faAward, faShieldAlt, faUsers, faTasks, faBalanceScale, faGlobe, faSearchPlus, faSyncAlt,
    faNotesMedical, faBiohazard, faHeartPulse, faUserMd, faBuilding, faFolderOpen, faPills, faLock
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Differentiators
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Planning Process
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Services Include Summary

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for medical equipment/planning
    const heroBgImage = 'https://images.unsplash.com/photo-1581092917219-05f80e6091ba?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing tech/equipment

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Why Cliniqor: Your Trusted Medical Equipment Planning Partner</h1>
            <p className="cliniqor-sub-heading">
              Comprehensive Understanding, Customized Solutions, Future-Forward Approach
            </p>
            <p className="cliniqor-intro-text">
              Cliniqor offers meticulous medical equipment planning considering the latest tech and long-term sustainability. Our expertise spans clinics to large hospitals, ensuring optimal resources for any healthcare setting.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- KEY DIFFERENTIATORS (Features Section - 7 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Key Differentiators</h2>
              <p>What sets Cliniqor apart in medical equipment planning.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Adjusted grid for potentially 7 items */}
            <style>{`.cliniqor-features-grid { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); } @media (min-width: 992px) { .cliniqor-features-grid { grid-template-columns: repeat(4, 1fr); } }`}</style> {/* Example: 4 cols on large screens */}
            <div className="cliniqor-features-grid">
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                    <h4>1. Comprehensive Knowledge & Expertise</h4>
                    <p>Seasoned consultants familiar with critical care, OTs, labs, and general care areas.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLightbulb} /></div>
                    <h4>2. Future-Oriented Planning</h4>
                    <p>Designing for current needs and future growth, accommodating new tech without major restructuring.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <h4>3. Cost-Effective Solutions</h4>
                    <p>Sourcing high-quality, budget-aligned equipment through extensive supplier networks.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCog} /></div>
                    <h4>4. Customized Equipment Selection</h4>
                    <p>Tailoring selection to facility size, specialties, and demographics for optimal performance.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCalculator} /></div>
                    <h4>5. Comprehensive Budget Planning</h4>
                    <p>Detailed upfront cost assessments (50-60% of project cost) for optimal resource allocation.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHandshake} /></div>
                    <h4>6. Quality Assurance via Vendor Partnerships</h4>
                    <p>Strong relationships ensuring best-in-class, reliable, safe equipment with vendor comparisons.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptopCode} /></div>
                    <h4>7. Seamless Integration with IT & PACS</h4>
                    <p>Ensuring equipment compatibility with your facility’s digital infrastructure.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- PLANNING PROCESS (Detailed Planning Section - 9 steps) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Comprehensive Medical Equipment Planning Process</h2>
               <p>A structured approach addressing every aspect from concept to post-installation.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for Process Steps (9 items - will be long) */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faComments} /></div>
                    <div className="cliniqor-content"><h4>1. Consultation & Needs Assessment</h4> <p>Understanding facility services, patient population, and specialized requirements.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSitemap} /></div>
                    <div className="cliniqor-content"><h4>2. Department-Wise Equipment Planning</h4> <p>Creating detailed lists with biomedical engineers for all areas (general to specialized).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faWeightHanging} /></div>
                    <div className="cliniqor-content"><h4>3. Load Calculations & Space Planning</h4> <p>Ensuring facility support (power, structural) and optimizing equipment placement.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faShoppingCart} /></div>
                    <div className="cliniqor-content"><h4>4. Vendor Selection & Procurement</h4> <p>Providing comparisons, sourcing quality equipment at competitive prices via supplier network.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCog} /></div> {/* Cog for Pre-install */}
                    <div className="cliniqor-content"><h4>5. Pre-Installation Services & Coordination</h4> <p>Preparing infrastructure (electrical, mechanical, civil) and coordinating with vendors.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faWrench} /></div>
                    <div className="cliniqor-content"><h4>6. Installation Supervision & Quality Control</h4> <p>Overseeing setup per specs/regulations, ensuring safety and operational readiness.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <div className="cliniqor-content"><h4>7. Inventory Audit & Ongoing Maintenance</h4> <p>Verifying all installed items and providing services for continued operation and uptime.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faNetworkWired} /></div>
                    <div className="cliniqor-content"><h4>8. Post-Installation Integration</h4> <p>Ensuring seamless connection with IT systems and PACS for data flow.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                    <div className="cliniqor-content"><h4>9. Training and Support</h4> <p>Providing comprehensive staff training on equipment use, maintenance, and troubleshooting.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- SERVICES INCLUDE SUMMARY (Reusing Features Layout - 6 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Our Services Include:</h2>
                 <p>A summary of our comprehensive medical equipment planning offerings.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Service Summary --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTools} /></div>
                            <h4>Medical Equipment Planning</h4>
                            <p>Detailed, customized planning based on facility needs.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCalculator} /></div>
                            <h4>Budgeting & Cost Optimization</h4>
                            <p>Transparent budgeting ensuring cost-efficiency.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCog} /></div>
                            <h4>Pre-installation Services</h4>
                            <p>Ensuring infrastructure readiness (electrical, mechanical, civil).</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHandshake} /></div>
                            <h4>Vendor Selection & Procurement</h4>
                            <p>Thorough process for best equipment at competitive prices.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faWrench} /></div>
                            <h4>Installation Supervision</h4>
                            <p>Overseeing setup to ensure quality standards adherence.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLifeRing} /></div>
                            <h4>Maintenance & Support</h4>
                            <p>Ongoing support, audits, and training for operational excellence.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* --- CONCLUSION SECTION (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div className="cliniqor-section-title cliniqor-fade-in-up"> {/* No specific ref needed for conclusion title */}
                 <h2>Conclusion: Future-Proofing Your Healthcare Facility</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Effective medical equipment planning is crucial for delivering high-quality, efficient, and safe healthcare. Partnering with Cliniqor gives you access to best practices, industry expertise, and tailored solutions for peak facility performance.</p>
                   <p>From initial planning to post-installation support, we ensure your facility is equipped to provide the best patient care while optimizing resources and minimizing costs. Let Cliniqor guide you in equipping your facility for the future with precision, efficiency, and the highest quality standards.</p>
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Optimize Your Medical Equipment Strategy</h3>
          <p>Ensure your healthcare facility operates at peak performance with Cliniqor's expert medical equipment planning services. Contact us today to equip your facility for the future.</p>
          <a href="#contact" className="cliniqor-cta-button">Get Equipment Planning Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;